import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  TextField, Button, Box, Typography, Stack, Autocomplete, Paper, ButtonBase,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import { Close, Collections, Edit, Visibility } from '@mui/icons-material';
import { useFilter } from '../../../layouts/dashboard/FilterProvider';
import WhatsappApiFunctions from '../../../API/whatsapp';

const dropzoneStyle = {
  border: '2px dashed #cccccc',
  borderRadius: '4px',
  padding: '20px',
  textAlign: 'center',
  cursor: 'pointer',
  marginBottom: 2,
};

const URLs = process.env.REACT_APP_API_ENDPOINT;

const UpdateEvent = ({ onClose }) => {
  const { setFilter, filter } = useFilter();
  const [templateKey, setTemplateKey] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [eventKey, setEventKey] = useState('');
  const [eventName, setEventName] = useState('');
  const [templateList, setTemplateList] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedEventName, setSelectedEventName] = useState(null);
  const [imageEditable, setImageEditable] = useState(false);
  const [themeEditable, setThemeEditable] = useState(false);
  const [fontEditable, setFontEditable] = useState(false);
  const [isNameEditable, setIsNameEditable] = useState(false);
  const [updatedEventName, setUpdatedEventName] = useState(null);
  const [defaultValues, setDefaultValues] = useState({
    eventName: '',
    eventImage: '',
    themeId: '',
    fontId: '',
  });
  // const [selectedTemplate, setTemplateKey] = useState('');

  const [columns, setColumns] = useState([]);

  const [customImage, setCustomImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [disableButton, setDisableButton] = useState(false);
  const [loading, setLoading] = useState(false);

  // Theme and Font state
  const [themeList, setThemeList] = useState([]);
  const [fontList, setFontList] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedFont, setSelectedFont] = useState('');
  const [themePreviewOpen, setThemePreviewOpen] = useState(false);
  const [fontPreviewOpen, setFontPreviewOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    setCustomImage(acceptedFiles[0]);
    setPreviewImage(URL.createObjectURL(acceptedFiles[0]));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const isButtonDisabled = templateName === 'custom_runner_image'
    ? (!updatedEventName && (!selectedTheme || !selectedFont || (!customImage && !previewImage))) ||
      (updatedEventName && 
       updatedEventName?.trim() === defaultValues?.eventName?.trim() && 
       String(selectedTheme) === String(defaultValues?.themeId || '') && 
       String(selectedFont) === String(defaultValues?.fontId || '') &&
       !customImage && !previewImage)
    : customImage
    ? false
    : (!updatedEventName && customImage === null) ||
      (updatedEventName && updatedEventName?.trim() === defaultValues?.eventName?.trim());

  const updateImage = async (e) => {
    e.preventDefault();
    setDisableButton(true);
    setLoading(true);
    const accessToken = localStorage.getItem('accessToken');
    const formData = new FormData();
    formData.append('EventKey', String(eventKey));
    
    // For custom_runner_image template, use theme, font, and custom image
    if (templateName === 'custom_runner_image') {
      if (selectedTheme) formData.append('RunnerTemplateKey', Number(selectedTheme));
      if (selectedFont) formData.append('FontKey', Number(selectedFont));
      if (customImage) formData.append('CustomImage', customImage);
    } else if (customImage) {
      formData.append('CustomImage', customImage);
    }
    
    if (updatedEventName) {
      formData.append('EventName', String(updatedEventName));
    }

    try {
      const response = await fetch(`${URLs}Template/UpdateEvent`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      getEventList();
      // Reset state before closing
      setSelectedTheme('');
      setSelectedFont('');
      setPreviewImage(null);
      setCustomImage(null);
      setSelectedEvent(null);
      setUpdatedEventName(null);
      setEventKey(null);
      onClose(); // Close the modal after successful submission
      setFilter({ ...filter, eventCount: filter.eventCount + 1 });
      toast.success('Event updated successfully', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
      // Handle error (e.g., show an error message)
    } finally {
      setDisableButton(false);
      setLoading(false);
    }
  };

  async function getTempleList() {
    try {
      const response = await WhatsappApiFunctions.getTempleList();
      const { Templates } = response;
      setTemplateList(Templates);
    } catch (error) {
      console.error('Error fetching message count:', error);
    }
  }

  async function getEventList() {
    try {
      const response = await WhatsappApiFunctions.getEventList(templateKey);
      const { Events } = response;
      setEventList(Events);
    } catch (error) {
      console.error('Error fetching message count:', error);
    }
  }

  useEffect(() => {
    getTempleList();
  }, []);

  useEffect(() => {
    getEventList();
  }, [templateKey]);

  useEffect(() => {
    if (imageEditable) {
      setCustomImage(null);
    }
  }, [imageEditable]);

  useEffect(() => {
    // Reset edit states when template or event changes
    if (!templateName || templateName !== 'custom_runner_image') {
      setThemeEditable(false);
      setFontEditable(false);
      setSelectedTheme('');
      setSelectedFont('');
      setPreviewImage(null);
      setCustomImage(null);
    }
    if (!selectedEvent) {
      setThemeEditable(false);
      setFontEditable(false);
      setSelectedTheme('');
      setSelectedFont('');
      setPreviewImage(null);
      setCustomImage(null);
    }
  }, [templateName, selectedEvent]);

  // Parse and set CustomizedData after theme and font lists are loaded
  useEffect(() => {
    if (selectedEvent && templateName === 'custom_runner_image' && selectedEvent.CustomizedData) {
      try {
        const customizedData = JSON.parse(selectedEvent.CustomizedData);
        const runnerTemplateKey = customizedData?.RunnerTemplateKey;
        const fontKey = customizedData?.FontKey;
        const imagePath = customizedData?.ImagePath || '';
        
        // Map RunnerTemplateKey to theme ID from themeList (if list is loaded)
        let themeId = '';
        if (runnerTemplateKey) {
          if (themeList.length > 0) {
            const theme = themeList.find(t => 
              (t.id && Number(t.id) === Number(runnerTemplateKey)) || 
              (t.theme_id && Number(t.theme_id) === Number(runnerTemplateKey))
            );
            if (theme) {
              themeId = String(theme.id || theme.theme_id || runnerTemplateKey);
            } else {
              // If not found in list, use the key directly
              themeId = String(runnerTemplateKey);
            }
          } else {
            // List not loaded yet, use key directly
            themeId = String(runnerTemplateKey);
          }
          
          // Only set if not already set or if lists have loaded and we need to remap
          if (themeId && (!selectedTheme || (themeList.length > 0 && selectedTheme !== themeId))) {
            setSelectedTheme(themeId);
          }
        }
        
        // Map FontKey to font ID from fontList (if list is loaded)
        let fontId = '';
        if (fontKey) {
          if (fontList.length > 0) {
            const font = fontList.find(f => Number(f.id) === Number(fontKey));
            if (font) {
              fontId = String(font.id);
            } else {
              // If not found in list, use the key directly
              fontId = String(fontKey);
            }
          } else {
            // List not loaded yet, use key directly
            fontId = String(fontKey);
          }
          
          // Only set if not already set or if lists have loaded and we need to remap
          if (fontId && (!selectedFont || (fontList.length > 0 && selectedFont !== fontId))) {
            setSelectedFont(fontId);
          }
        }
        
        // Set image preview if image path exists
        if (imagePath && !previewImage) {
          setPreviewImage(imagePath);
        }
        
        // Update default values
        setDefaultValues((prev) => {
          const newThemeId = themeId || prev.themeId;
          const newFontId = fontId || prev.fontId;
          if (prev.themeId !== newThemeId || prev.fontId !== newFontId || prev.eventImage !== imagePath) {
            return {
              ...prev,
              themeId: newThemeId,
              fontId: newFontId,
              eventImage: imagePath,
            };
          }
          return prev;
        });
      } catch (error) {
        console.error('Error parsing CustomizedData:', error);
      }
    }
  }, [selectedEvent, templateName, themeList, fontList]);

  // Fetch themes and fonts when template is custom_runner_image
  useEffect(() => {
    const fetchThemeAndFonts = async () => {
      if (templateName === 'custom_runner_image') {
        const accessToken = localStorage.getItem('accessToken');
        
        // Fetch themes - try list-templates endpoint first, then fallback to GetTheme
        try {
          // Try list-templates endpoint (similar to list-fonts)
          const themeListResponse = await fetch(`${URLs}Font/list-templates`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (themeListResponse.ok) {
            const themeListData = await themeListResponse.json();
            if (themeListData && themeListData.Templates && Array.isArray(themeListData.Templates)) {
              setThemeList(themeListData.Templates);
            } else if (Array.isArray(themeListData)) {
              setThemeList(themeListData);
            } else {
              setThemeList([]);
            }
          } else {
            // Fallback to GetTheme endpoint
            const themeResponse = await fetch(`${URLs}Settings/GetTheme`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            if (themeResponse.ok) {
              const themeData = await themeResponse.json();
              setCurrentTheme(themeData);
              // If single theme object, wrap it in array
              if (themeData && Array.isArray(themeData)) {
                setThemeList(themeData);
              } else if (themeData && themeData.Themes && Array.isArray(themeData.Themes)) {
                setThemeList(themeData.Themes);
              } else {
                setThemeList(themeData ? [themeData] : []);
              }
            }
          }
        } catch (err) {
          console.error('Error fetching theme:', err);
          // Try fallback to GetTheme
          try {
            const themeResponse = await fetch(`${URLs}Settings/GetTheme`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            if (themeResponse.ok) {
              const themeData = await themeResponse.json();
              setCurrentTheme(themeData);
              setThemeList(themeData ? [themeData] : []);
            }
          } catch (fallbackErr) {
            console.error('Error fetching theme fallback:', fallbackErr);
          }
        }

        // Fetch fonts
        try {
          const fontResponse = await fetch(`${URLs}Font/list-fonts`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (fontResponse.ok) {
            const fontData = await fontResponse.json();
            if (fontData && fontData.Fonts && Array.isArray(fontData.Fonts)) {
              setFontList(fontData.Fonts);
            } else if (Array.isArray(fontData)) {
              setFontList(fontData);
            } else {
              setFontList([]);
            }
          }
        } catch (err) {
          console.error('Error fetching fonts:', err);
        }
      } else {
        // Reset when not custom_runner_image
        setThemeList([]);
        setFontList([]);
        setSelectedTheme('');
        setSelectedFont('');
      }
    };

    fetchThemeAndFonts();
  }, [templateName]);

  // Memoize validated theme value to prevent FormControl errors
  const validatedThemeValue = useMemo(() => {
    if (!selectedTheme || !themeList?.length) return '';
    const theme = themeList.find(t => 
      String(t.id || t.theme_id || '') === String(selectedTheme)
    );
    return theme ? String(theme.id || theme.theme_id || '') : '';
  }, [selectedTheme, themeList]);

  // Memoize validated font value to prevent FormControl errors
  const validatedFontValue = useMemo(() => {
    if (!selectedFont || !fontList?.length) return '';
    const font = fontList.find(f => String(f.id) === String(selectedFont));
    return font ? String(font.id) : '';
  }, [selectedFont, fontList]);

  return (
    <Stack gap={2} component="form" onSubmit={updateImage} sx={{ mt: 2 }}>
      <Autocomplete
        options={templateList}
        required
        disabled={disableButton}
        getOptionLabel={(option) => option?.TemplateName} // Display TemplateName in the input field
        renderInput={(params) => (
          <TextField
            {...params}
            label="Template"
            disabled={disableButton}
            required
            autoFocus
            variant="outlined"
            sx={{ width: '100%' }}
          />
        )}
        onChange={(event, value) => {
          if (value) {
            setTemplateKey(value.MessageTemplateKey); // Store the selected template in state
            setTemplateName(value.TemplateName || ''); // Store template name to check if it's custom_runner_image
          } else {
            setTemplateKey(null); // Reset if no value is selected
            setTemplateName(''); // Reset template name
          }
        }}
      />
      <Stack
        direction="row"
        sx={{
          width: '100%',
          alignItems: 'center',
          gap: 1, // Adds spacing between components
        }}
      >
        {/* Autocomplete or +TextField based on isNameEditable */}
        {!isNameEditable ? (
          <Autocomplete
            options={eventList || []}
            label="Event Name"
            value={selectedEvent}
            disabled={!templateKey || disableButton}
            getOptionLabel={(option) => option?.EventsName || ''}
            renderInput={(params) => <TextField {...params} label="Event" variant="outlined" sx={{ flexGrow: 1 }} />}
            onChange={(event, value) => {
              setSelectedEvent(value);
              setUpdatedEventName(value?.EventsName || null);
              setEventKey(value?.EventsDetailKey || null);
              
              // Parse CustomizedData for custom_runner_image template
              let themeId = '';
              let fontId = '';
              let imagePath = '';
              
              if (value?.CustomizedData && templateName === 'custom_runner_image') {
                try {
                  const customizedData = JSON.parse(value.CustomizedData);
                  const runnerTemplateKey = customizedData?.RunnerTemplateKey;
                  const fontKey = customizedData?.FontKey;
                  imagePath = customizedData?.ImagePath || '';
                  
                  // Map RunnerTemplateKey to theme ID from themeList
                  if (runnerTemplateKey && themeList.length > 0) {
                    const theme = themeList.find(t => 
                      (t.id && Number(t.id) === Number(runnerTemplateKey)) || 
                      (t.theme_id && Number(t.theme_id) === Number(runnerTemplateKey))
                    );
                    if (theme) {
                      themeId = String(theme.id || theme.theme_id || runnerTemplateKey);
                      setSelectedTheme(themeId);
                    } else {
                      // If not found in list, use the key directly
                      themeId = String(runnerTemplateKey);
                      setSelectedTheme(themeId);
                    }
                  } else if (runnerTemplateKey) {
                    // Store the key temporarily, will be mapped when list loads
                    themeId = String(runnerTemplateKey);
                  }
                  
                  // Map FontKey to font ID from fontList
                  if (fontKey && fontList.length > 0) {
                    const font = fontList.find(f => Number(f.id) === Number(fontKey));
                    if (font) {
                      fontId = String(font.id);
                      setSelectedFont(fontId);
                    } else {
                      // If not found in list, use the key directly
                      fontId = String(fontKey);
                      setSelectedFont(fontId);
                    }
                  } else if (fontKey) {
                    // Store the key temporarily, will be mapped when list loads
                    fontId = String(fontKey);
                  }
                  
                  // Set image preview if image path exists
                  if (imagePath) {
                    setPreviewImage(imagePath);
                  }
                } catch (error) {
                  console.error('Error parsing CustomizedData:', error);
                }
              } else {
                // Fallback to direct ThemeId/FontId if CustomizedData doesn't exist
                themeId = value?.ThemeId ? String(value.ThemeId) : '';
                fontId = value?.FontId ? String(value.FontId) : '';
                
                if (themeId) {
                  setSelectedTheme(themeId);
                }
                if (fontId) {
                  setSelectedFont(fontId);
                }
              }
              
              setDefaultValues((prev) => ({ 
                ...prev, 
                eventName: value.EventsName,
                themeId,
                fontId,
                eventImage: imagePath,
              }));
            }}
            sx={{ flexGrow: 1 }} // Makes it take remaining space
          />
        ) : (
          <TextField
            label="Event Name"
            value={updatedEventName}
            focused
            onChange={(e) => setUpdatedEventName(e.target.value)}
            variant="outlined"
            sx={{ flexGrow: 1 }}
          />
        )}

        {/* Edit / Close Button */}
        <ButtonBase
          sx={{
            padding: '0.5rem',
            borderRadius: '8px',
            background: updatedEventName
              ? defaultValues?.eventName?.trim() !== updatedEventName?.trim()
                ? '#ffc4c485'
                : '#f8f8f8'
              : '#f8f8f8',

            width: '3rem',
            height: '3rem',
            opacity: selectedEvent ? 1 : 0.5, // Faded look when disabled
            pointerEvents: selectedEvent ? 'auto' : 'none', // Disable button click when no selection
          }}
          onClick={() => {
            setIsNameEditable((prev) => {
              if (!prev && selectedEvent) {
                setUpdatedEventName(selectedEvent?.EventsName);
              }
              return !prev;
            });
          }}
          disabled={!selectedEvent} // Button disabled if no event is selected
        >
          {isNameEditable ? (
            <Close sx={{ color: defaultValues.eventName.trim() !== updatedEventName.trim() && 'red' }} />
          ) : (
            <Edit sx={{ fontSize: '1.2rem' }} />
          )}
        </ButtonBase>
      </Stack>

      {/* Show theme and font selectors for custom_runner_image template, otherwise show image section */}
      {templateName === 'custom_runner_image' && selectedEvent ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl fullWidth disabled={disableButton || (!themeEditable && selectedTheme && defaultValues.themeId)} required>
              <InputLabel id="theme-select-label">Theme</InputLabel>
              <Select
                labelId="theme-select-label"
                value={validatedThemeValue}
                label="Theme"
                onChange={(e) => setSelectedTheme(e.target.value)}
                renderValue={(value) => {
                  if (!value || !themeList?.length) return '';
                  const theme = themeList.find(t => 
                    String(t.id || t.theme_id || '') === String(value)
                  );
                  return theme ? (theme.name || theme.template_name || theme.theme_name || `Theme ${value}`) : '';
                }}
              >
                {themeList?.map((theme, index) => {
                  const themeValue = String(theme.id || theme.theme_id || index);
                  return (
                    <MenuItem key={theme.id || theme.theme_id || index} value={themeValue}>
                      {theme.name || theme.template_name || theme.theme_name || `Theme ${index + 1}`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {selectedTheme && (
              <IconButton
                onClick={() => setThemePreviewOpen(true)}
                disabled={disableButton}
                color="primary"
                sx={{ alignSelf: 'center' }}
              >
                <Visibility />
              </IconButton>
            )}
            {eventKey && templateKey && (
              <ButtonBase
                sx={{ padding: '0.5rem', borderRadius: '8px', background: '#f8f8f8', alignSelf: 'center' }}
                onClick={() => {
                  if (themeEditable) {
                    // Reset to original value when closing edit mode
                    setSelectedTheme(defaultValues.themeId || '');
                  }
                  setThemeEditable((prev) => !prev);
                }}
              >
                {themeEditable ? <Close /> : <Edit sx={{ fontSize: '1.2rem' }} />}
              </ButtonBase>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl fullWidth disabled={disableButton || (!fontEditable && selectedFont && defaultValues.fontId)} required>
              <InputLabel id="font-select-label">Font</InputLabel>
              <Select
                labelId="font-select-label"
                value={validatedFontValue}
                label="Font"
                onChange={(e) => setSelectedFont(e.target.value)}
                renderValue={(value) => {
                  if (!value || !fontList?.length) return '';
                  const font = fontList.find(f => String(f.id) === String(value));
                  return font ? font.font_name : '';
                }}
              >
                {fontList?.map((font) => (
                  <MenuItem key={font.id} value={String(font.id)}>
                    {font.font_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedFont && (
              <IconButton
                onClick={() => setFontPreviewOpen(true)}
                disabled={disableButton}
                color="primary"
                sx={{ alignSelf: 'center' }}
              >
                <Visibility />
              </IconButton>
            )}
            {eventKey && templateKey && (
              <ButtonBase
                sx={{ padding: '0.5rem', borderRadius: '8px', background: '#f8f8f8', alignSelf: 'center' }}
                onClick={() => {
                  if (fontEditable) {
                    // Reset to original value when closing edit mode
                    setSelectedFont(defaultValues.fontId || '');
                  }
                  setFontEditable((prev) => !prev);
                }}
              >
                {fontEditable ? <Close /> : <Edit sx={{ fontSize: '1.2rem' }} />}
              </ButtonBase>
            )}
          </Stack>

          <Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', mb: 1 }}>
              <Typography sx={{ width: 'max-content' }}>Event Image</Typography>
              {eventKey && templateKey && (
                <ButtonBase
                  sx={{ padding: '0.5rem', borderRadius: '8px', background: '#f8f8f8' }}
                  onClick={() => {
                    setImageEditable((prev) => !prev);
                  }}
                >
                  {imageEditable ? <Close /> : <Edit sx={{ fontSize: '1.2rem' }} />}
                </ButtonBase>
              )}
            </Stack>
            {imageEditable || !selectedEvent?.CustomizedData ? (
              <Box {...getRootProps()} sx={dropzoneStyle}>
                <input {...getInputProps()} />
                {isDragActive ? (
                  <Typography>Drop the image here ...</Typography>
                ) : previewImage ? (
                  <>
                    <img
                      src={previewImage}
                      alt="preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        maxHeight: '200px',
                      }}
                    />
                    {customImage?.name && (
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Selected file: {customImage.name}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography>Drag 'n' drop an image here, or click to select from gallery</Typography>
                )}
              </Box>
            ) : (
              <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                {(() => {
                  try {
                    const customizedData = selectedEvent?.CustomizedData ? JSON.parse(selectedEvent.CustomizedData) : null;
                    const imagePath = customizedData?.ImagePath;
                    return imagePath ? (
                      <img
                        src={imagePath}
                        alt="preview"
                        style={{
                          width: '100%',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          objectFit: 'contain',
                        }}
                      />
                    ) : null;
                  } catch (error) {
                    return null;
                  }
                })()}
              </Paper>
            )}
          </Stack>
        </>
      ) : (
        <Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
            <Typography sx={{ width: 'max-content' }}>Selected Event Image</Typography>
            {eventKey && templateKey && (
              <ButtonBase
                sx={{ padding: '0.5rem', borderRadius: '8px', background: '#f8f8f8' }}
                onClick={() => {
                  setImageEditable((prev) => !prev);
                }}
              >
                {imageEditable ? <Close /> : <Edit sx={{ fontSize: '1.2rem' }} />}
              </ButtonBase>
            )}
          </Stack>
          <Paper
            elevation={4}
            sx={{
              width: '100%',
              maxHeight: '',
              borderRadius: '8px',
              marginTop: '0.5rem',
              padding: selectedEvent ? '1rem' : '0',
            }}
          >
            {selectedEvent ? (
              imageEditable || selectedEvent?.CustomizedData == null ? (
                <>
                  <Typography mb={1}>Update your event image</Typography>
                  <Box {...getRootProps()} sx={dropzoneStyle}>
                    <input {...getInputProps()} />
                    {isDragActive ? (
                      <Typography>Drop the image here ...</Typography>
                    ) : previewImage ? (
                      <>
                        <img
                          src={previewImage}
                          alt="preview"
                          style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            maxHeight: '200px',
                          }}
                        />
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Selected file: {customImage?.name}
                        </Typography>
                      </>
                    ) : (
                      <Typography>Drag 'n' drop an image here, or click to select from gallery</Typography>
                    )}
                  </Box>
                </>
              ) : (
                <img
                  src={JSON.parse(selectedEvent?.CustomizedData).ImagePath}
                  alt="preview"
                  style={{
                    width: '100%',
                    maxHeight: '60vh',
                    aspectRatio: '1/1',
                    background: '#efeeee',
                    borderRadius: '12px',
                    objectFit: 'contain',
                  }}
                />
              )
            ) : (
              <Stack alignItems="center">
                <Collections sx={{ fontSize: '14rem', color: '#e5e8eb' }} />
                <Typography sx={{ color: 'grey', alignItems: 'center' }}>
                  Select Template and Event to preview the event image
                </Typography>
              </Stack>
            )}
          </Paper>
        </Stack>
      )}

      {/* Theme Preview Dialog */}
      <Dialog 
        open={themePreviewOpen} 
        onClose={() => setThemePreviewOpen(false)} 
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: '60vw',
            maxHeight: '60vh',
            width: 'auto',
            m: 2,
          }
        }}
      >
        <DialogTitle>Theme Preview</DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', maxHeight: 'calc(60vh - 120px)', overflow: 'auto' }}>
          {selectedTheme && themeList.find(t => (t.id || t.theme_id) === Number(selectedTheme)) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              {(() => {
                const theme = themeList.find(t => (t.id || t.theme_id) === Number(selectedTheme));
                return (
                  <>
                    <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                      {theme.name || theme.template_name || theme.theme_name || 'Theme Preview'}
                    </Typography>
                    {theme.template_url || theme.template_file_url || theme.theme_url ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <img
                          src={theme.template_url || theme.template_file_url || theme.theme_url}
                          alt="Theme preview"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: 'calc(60vh - 120px)', 
                            height: 'auto',
                            borderRadius: 8, 
                            objectFit: 'contain',
                            display: 'block'
                          }}
                        />
                      </Box>
                    ) : (
                      <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                          {JSON.stringify(theme, null, 2)}
                        </pre>
                      </Paper>
                    )}
                  </>
                );
              })()}
            </Box>
          ) : currentTheme ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                {currentTheme.name || currentTheme.template_name || 'Theme Preview'}
              </Typography>
              {currentTheme.template_url || currentTheme.template_file_url || currentTheme.theme_url ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <img
                    src={currentTheme.template_url || currentTheme.template_file_url || currentTheme.theme_url}
                    alt="Theme preview"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 'calc(60vh - 120px)', 
                      height: 'auto',
                      borderRadius: 8, 
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                </Box>
              ) : (
                <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                    {JSON.stringify(currentTheme, null, 2)}
                  </pre>
                </Paper>
              )}
            </Box>
          ) : (
            <Typography>No theme available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThemePreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Font Preview Dialog */}
      <Dialog open={fontPreviewOpen} onClose={() => setFontPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Font Preview</DialogTitle>
        <DialogContent>
          {selectedFont && fontList.find(f => f.id === Number(selectedFont)) ? (
            <Box>
              {(() => {
                const font = fontList.find(f => f.id === Number(selectedFont));
                return (
                  <>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {font.font_name}
                    </Typography>
                    <style>
                      {`
                        @font-face {
                          font-family: 'PreviewFont-${font.id}';
                          src: url('${font.font_file_url}');
                        }
                      `}
                    </style>
                    <Paper sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Typography sx={{ fontFamily: `'PreviewFont-${font.id}'`, fontSize: '24px', mb: 1 }}>
                        The quick brown fox jumps over the lazy dog
                      </Typography>
                      <Typography sx={{ fontFamily: `'PreviewFont-${font.id}'`, fontSize: '18px', mb: 1 }}>
                        ABCDEFGHIJKLMNOPQRSTUVWXYZ
                      </Typography>
                      <Typography sx={{ fontFamily: `'PreviewFont-${font.id}'`, fontSize: '18px' }}>
                        abcdefghijklmnopqrstuvwxyz 0123456789
                      </Typography>
                    </Paper>
                  </>
                );
              })()}
            </Box>
          ) : (
            <Typography>No font selected</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFontPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* {customImage && (
        <>
          <img
            src={previewImage}
            alt="preview-image"
            style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: '8px', maxHeight: '200px' }}
          />
          <Typography variant="body2" sx={{ mb: 2 }}>
            Selected file: {customImage.name}
          </Typography>
        </>
      )} */}
      <Button
        disabled={
          loading || 
          disableButton || 
          !(templateKey && eventKey) || 
          isButtonDisabled ||
          (templateName === 'custom_runner_image' && (!selectedTheme || !selectedFont || (!customImage && !previewImage)))
        }
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
      >
        {loading ? <div className="loader" /> : <></>}
        Submit
      </Button>
    </Stack>
  );
};

export default UpdateEvent;
